<template>
  <div v-if="isVisible" class="modal-overlay">
    <div class="modal-dialog mandatory-completion-modal" @click.stop>
        <div class="uk-modal-header">
          <h4 class="uk-modal-title">
            <i class="uil-exclamation-triangle"></i>
            {{ $t('general.mandatory_missions') }}
          </h4>
        </div>
        <div class="uk-modal-body">
          <!-- Notice Section -->
          <div v-if="unreadNotices.length > 0" class="notice-section">
            <h5 class="section-title">
              <i class="uil-bell"></i>
              {{ $t('general.notifications_announcements') }}
            </h5>
            <div v-for="notice in unreadNotices" :key="notice.id" class="notice-item">
              <div class="notice-header">
                <h6 class="notice-title">{{ notice.name }}</h6>
              </div>
              <div class="notice-content" v-html="notice.text"></div>
              <div class="notice-actions">
                <button
                    class="uk-button uk-button-small uk-button-primary mark-read-btn"
                    @click.prevent="markAsRead(notice.id)"
                >
                  <i class="uil-check"></i>
                  {{ $t('general.mark_as_read') }}
                </button>
              </div>
            </div>
          </div>

          <!-- Assignments Section -->
          <div v-if="shouldShowAssignments" class="assignments-section">
            <h5 class="section-title">
              <i class="uil-graduation-cap"></i>
              {{ $t('general.mandatory_assignments') }}
            </h5>
            <div class="assignments-list">
              <div v-for="assignment in assignments" :key="assignment.id" class="assignment-item">
                <div class="assignment-image">
                  <img :alt="assignment.name" :src="assignment.cover_image_link"/>
                </div>
                <div class="assignment-info">
                  <h6 class="assignment-title">{{ assignment.name }}</h6>
                  <p class="assignment-description">{{ assignment.description }}</p>
                  <div class="assignment-meta">
                    <span v-if="assignment.start_date" class="assignment-start-date">
                      <i class="uil-calendar-alt"></i>
                      {{ $t('general.start_date') }}: {{ formatDate(assignment.start_date) }}
                    </span>
                    <span v-if="assignment.end_date" class="assignment-end-date">
                      <i class="uil-calendar-alt"></i>
                      {{ $t('general.end_date') }}: {{ formatDate(assignment.end_date) }}
                    </span>
                    <span v-if="assignment.duration > 0" class="assignment-duration">
                      <i class="uil-clock"></i>
                      {{ assignment.duration }} {{ $t('general.minutes') }}
                    </span>
                  </div>
                </div>
                <div class="assignment-actions">
                  <button
                      :class="getAssignmentButtonClass(assignment.action_label)"
                      @click.prevent="startAssignment(assignment)"
                  >
                    <i :class="getAssignmentButtonIcon(assignment.action_label)"></i>
                    {{ $t(`general.${assignment.action_label}`) }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { showSuccess, showError } from "@/utils/alertUtils";
import { MARK_NOTICE_AS_READ, FETCH_MANDATORY_COMPLETIONS } from '@/core/services/store/mandatory.module';

export default {
  name: "MandatoryCompletionModal",
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isVisible: false
    };
  },
  watch: {
    visible(newVal) {
      this.isVisible = newVal;
    }
  },
  mounted() {
    this.isVisible = this.visible;
  },
  computed: {
    unreadNotices() {
      return this.$store.getters['mandatory/notices'] || [];
    },
    assignments() {
      return this.$store.getters['mandatory/assignments'] || [];
    },
    shouldShowAssignments() {
      // Eğer okunmamış notices varsa assignments'ı gösterme
      if (this.unreadNotices.length > 0) {
        return false; // Okunmamış notices var, assignments'ı gösterme
      }
      
      // Okunmamış notices yoksa assignments'ı göster
      return this.assignments && this.assignments.length > 0;
    },
  },
  methods: {
    formatDate(dateString) {
      return moment(dateString).locale(this.$i18n.locale).format('DD MMMM YYYY');
    },
    async markAsRead(noticeId) {
      try {
        await this.$store.dispatch(`mandatory/${MARK_NOTICE_AS_READ}`, noticeId);
        showSuccess(this.$t('report.success'));
        
        // Tüm verileri tekrar getir
        await this.$store.dispatch(`mandatory/${FETCH_MANDATORY_COMPLETIONS}`);
        
        // Tüm notices okunmuş mu kontrol et
        const allNoticesRead = this.unreadNotices.length === 0;
        
        if (allNoticesRead && this.assignments.length === 0) {
          // Tüm notices okunmuş ve assignments yoksa modal'ı kapat
          this.closeModal();
        }
      } catch (error) {
        console.error('Notice update error:', error);
        showError(this.$t('general.error_occurred'));
      }
    },
    startAssignment(assignment) {
      this.closeModal();


      this.$router.push({  name: 'user.assignment-detail', params: { id: assignment.id } });


    },
    getAssignmentButtonClass(actionLabel) {
      const baseClass = 'uk-button uk-button-small';
      
      switch (actionLabel) {
        case 'start':
          return `${baseClass} uk-button-primary`; // Mavi - başla
        case 'continue':
          return `${baseClass} uk-button-warning`; // Sarı - devam et
        default:
          return `${baseClass} uk-button-primary`; // Varsayılan mavi
      }
    },
    getAssignmentButtonIcon(actionLabel) {
      switch (actionLabel) {
        case 'start':
          return 'uil-play'; // Oynat ikonu
        case 'continue':
          return 'uil-redo'; // Devam et ikonu
        default:
          return 'uil-play'; // Varsayılan oynat ikonu
      }
    },
    closeModal() {
      this.isVisible = false;
      this.$emit('closed');
    },
  }
}
</script>

<style scoped>
/* Modal overlay */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000000;
}

/* Modal dialog */
.modal-dialog {
  background: white;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  max-width: 900px;
  width: 90vw;
  max-height: 90vh;
  overflow: hidden;
  position: relative;
  pointer-events: auto !important;
}

.mandatory-completion-modal {
  max-width: 900px !important;
  width: 90vw !important;
}

/* Tüm button'ları aktif hale getir */
.mandatory-completion-modal button {
  pointer-events: auto !important;
  cursor: pointer !important;
  opacity: 1 !important;
  user-select: none;
}

.mandatory-completion-modal .uk-modal-body {
  max-height: 70vh;
  overflow-y: auto;
  padding: 20px 30px;
  overflow-x: hidden;
}

.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-title i {
  color: #666CFF;
}

.notice-section {
  margin-bottom: 30px;
  padding: 0;
  background-color: transparent;
  border: none;
}

.notice-item {
  margin-bottom: 8px;
  padding: 10px 12px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  position: relative;
  border-left: 3px solid #3b82f6;
}

.notice-item::before {
  content: '';
  position: absolute;
  top: 8px;
  right: 8px;
  width: 6px;
  height: 6px;
  background: #ef4444;
  border-radius: 50%;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7);
  }
  70% {
    box-shadow: 0 0 0 4px rgba(239, 68, 68, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
  }
}

.notice-item:hover {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-left-color: #1d4ed8;
}

.notice-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.notice-title {
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  line-height: 1.3;
}

.notice-content {
  background-color: transparent;
  color: #6b7280;
  padding: 0;
  border-radius: 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 11px;
  line-height: 1.4;
  border: none;
  margin-bottom: 8px;
  box-shadow: none;
  font-weight: 400;
  margin-right: 16px;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.notice-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 6px;
}

.mark-read-btn {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 0.65rem;
  padding: 3px 6px;
  border-radius: 3px;
  font-weight: 400;
  transition: all 0.2s ease;
  min-height: 20px;
  background: #3b82f6;
  border: none;
  color: white;
  line-height: 1;
}

.mark-read-btn:not(:disabled):hover {
  background: #1d4ed8;
  transform: scale(1.02);
}

.mark-read-btn:disabled {
  background: #10b981;
  border: none;
  opacity: 0.7;
  cursor: not-allowed;
  font-size: 0.6rem;
}

.mark-read-btn i {
  font-size: 0.6rem;
}

.assignments-section {
  margin-bottom: 20px;
}

.assignments-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.assignment-item {
  display: flex;
  align-items: center;
  padding: 15px;
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.assignment-item:hover {
  background-color: #e9ecef;
  border-color: #666CFF;
}

.assignment-image {
  width: 60px;
  height: 60px;
  margin-right: 15px;
  flex-shrink: 0;
}

.assignment-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 6px;
}

.assignment-info {
  flex: 1;
  margin-right: 15px;
}

.assignment-title {
  font-size: 1rem;
  font-weight: 600;
  color: #495057;
  margin: 0 0 5px 0;
}

.assignment-description {
  font-size: 0.875rem;
  color: #6c757d;
  margin: 0 0 8px 0;
  line-height: 1.4;
}

.assignment-meta {
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 0.8rem;
  color: #6c757d;
}

.assignment-meta span {
  display: flex;
  align-items: center;
  gap: 4px;
}

.assignment-meta i {
  font-size: 12px;
}

.assignment-start-date {
  color: #10b981; /* Yeşil - başlangıç tarihi */
}

.assignment-end-date {
  color: #ef4444; /* Kırmızı - bitiş tarihi */
}

.assignment-duration {
  color: #6b7280; /* Gri - süre */
}

.assignment-actions {
  flex-shrink: 0;
}

.assignment-actions .uk-button {
  display: flex;
  align-items: center;
  gap: 5px;
  pointer-events: auto !important;
  cursor: pointer !important;
  opacity: 1 !important;
}

/* Assignment button özel renkleri */
.assignment-actions .uk-button-primary {
  background-color: #3b82f6; /* Mavi - Başla */
  border-color: #3b82f6;
}

.assignment-actions .uk-button-primary:hover {
  background-color: #1d4ed8;
  border-color: #1d4ed8;
}

.assignment-actions .uk-button-warning {
  background-color: #f59e0b; /* Sarı - Devam Et */
  border-color: #f59e0b;
  color: white;
}

.assignment-actions .uk-button-warning:hover {
  background-color: #d97706;
  border-color: #d97706;
}

.uk-modal-footer {
  padding: 15px 30px;
  border-top: 1px solid #e9ecef;
  background-color: #f8f9fa;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* Responsive */
@media (max-width: 768px) {
  .assignment-item {
    flex-direction: column;
    text-align: center;
  }

  .assignment-image {
    margin-right: 0;
    margin-bottom: 10px;
  }

  .assignment-info {
    margin-right: 0;
    margin-bottom: 10px;
  }

  .assignment-meta {
    justify-content: center;
  }
}
</style>
